import React from 'react';
import { useTranslation } from 'react-i18next';
import {
  Alert, Button, DialogActions, DialogContent, DialogTitle,
  Divider, IconButton, LinearProgress, Stack, Typography,
} from '@mui/material';
import CloseRoundedIcon from '@mui/icons-material/CloseRounded';

import { translateMessage } from '../../../shared/api';
import AppDialog from '../../../shared/components/AppDialog';
import ProcessingCloseWarningDialog from '../../../shared/components/ProcessingCloseWarningDialog';
import useProcessingCloseGuard from '../../../shared/hooks/useProcessingCloseGuard';
import { formatSize } from '../../../shared/utils/number';
import useResultSummary from '../hooks/useResultSummary';
import { getImportResultPresentation } from '../model/importResultPresentation';
import { DATA_SOURCE_IDS } from '../model/dataInputModel';
import ResultSummaryTable from './ResultSummaryTable';

function ImportSummary({ sourceTitle, year, fileName, fileSize }) {
  const { t } = useTranslation();
  return (
    <Stack spacing={1.5}>
      <Stack spacing={0.25}>
        <Typography variant="caption" color="text.secondary">
          {t('dataInput.uploadConfirm.source')}
        </Typography>
        <Typography variant="body2" fontWeight={700}>{sourceTitle}</Typography>
      </Stack>
      <Stack spacing={0.25}>
        <Typography variant="caption" color="text.secondary">
          {t('dataInput.reportingYear')}
        </Typography>
        <Typography variant="body2" fontWeight={700}>{year}</Typography>
      </Stack>
      <Stack spacing={0.25}>
        <Typography variant="caption" color="text.secondary">
          {t('dataInput.uploadConfirm.file')}
        </Typography>
        <Typography variant="body2" fontWeight={700} sx={{ overflowWrap: 'anywhere' }}>
          {fileName || t('dataInput.history.notAvailable')}
        </Typography>
        {fileSize && <Typography variant="caption" color="text.secondary">{fileSize}</Typography>}
      </Stack>
    </Stack>
  );
}

function SnapshotSummary({ sourceTitle, year, files }) {
  const { t } = useTranslation();
  return (
    <Stack spacing={1.5}>
      <Stack spacing={0.25}>
        <Typography variant="caption" color="text.secondary">
          {t('dataInput.uploadConfirm.source')}
        </Typography>
        <Typography variant="body2" fontWeight={700}>{sourceTitle}</Typography>
      </Stack>
      <Stack spacing={0.25}>
        <Typography variant="caption" color="text.secondary">
          {t('dataInput.reportingYear')}
        </Typography>
        <Typography variant="body2" fontWeight={700}>{year}</Typography>
      </Stack>
      <Stack spacing={0.25}>
        <Typography variant="caption" color="text.secondary">
          {t('dataInput.uploadConfirm.snapshotFiles')}
        </Typography>
        <Stack divider={<Divider flexItem />} spacing={0.5}>
          {files.map((item) => (
            <Stack
              key={item.key}
              direction="row"
              alignItems="center"
              justifyContent="space-between"
              spacing={2}
              sx={{ py: 0.5 }}
            >
              <Typography variant="body2" sx={{ overflowWrap: 'anywhere', minWidth: 0 }}>
                {item.name}
              </Typography>
              <Typography variant="caption" color="text.secondary" sx={{ flexShrink: 0 }}>
                {t(item.isRetained
                  ? 'dataInput.scope3Af.retainedFile'
                  : 'dataInput.scope3Af.localFile')}
                {item.size ? ` · ${formatSize(item.size)}` : ''}
              </Typography>
            </Stack>
          ))}
        </Stack>
      </Stack>
    </Stack>
  );
}

export default function UploadConfirmDialog({
  open,
  year,
  sourceId,
  sourceTitle,
  file,
  currentName,
  replacesYear,
  snapshotMode = false,
  snapshotChanged = false,
  files = [],
  operation,
  result,
  feedback,
  isProcessing = false,
  monitoringError = null,
  onClose,
  onExited,
  onConfirm,
  onRetryStatus,
  onReviewHistory,
  onRefreshData,
}) {
  const { t } = useTranslation();
  const resultPresentation = getImportResultPresentation(result);
  const isTerminal = Boolean(resultPresentation);
  const showProcessing = isProcessing && !isTerminal;
  const {
    requestClose, isWarningOpen, stay, closeAnyway,
  } = useProcessingCloseGuard({ isProcessing: showProcessing, onClose });
  const displayOperation = isTerminal ? result : operation;
  const terminalStatus = result?.status;
  const activeStatus = ['QUEUED', 'PROCESSING'].includes(operation?.status)
    ? operation.status
    : operation ? 'PROCESSING' : 'SUBMITTING';
  const isPartial = isTerminal && (
    terminalStatus === 'PARTIAL' || Boolean(result?.partialDataPossible)
  );
  const recalcOutcome = result?.recalculation?.outcome;
  const isAfReport = sourceId === DATA_SOURCE_IDS.SCOPE_3_AF_REPORT;
  const showResultTable = isTerminal && (
    isPartial
    || (terminalStatus === 'SUCCESS' && (recalcOutcome === 'SUCCESS' || isAfReport))
  );
  const {
    data: resultData,
    loading: resultLoading,
    error: resultError,
    reload: reloadResult,
  } = useResultSummary({ sourceId, year, enabled: open && showResultTable });
  const isConfirming = !showProcessing && !isTerminal;
  const fileName = file?.name || displayOperation?.fileName;
  const fileSize = formatSize(file?.size);
  const processedRows = Number(operation?.counters?.processed_rows);
  const totalRows = Number(operation?.counters?.total_rows);
  const hasDeterminateProgress = Number.isFinite(processedRows)
    && Number.isFinite(totalRows)
    && totalRows > 0;
  const progressValue = hasDeterminateProgress
    ? Math.min(100, Math.max(0, (processedRows / totalRows) * 100))
    : 0;
  const titleKey = isTerminal
    ? 'dataInput.importDialog.resultTitle'
    : showProcessing
      ? 'dataInput.importDialog.processingTitle'
      : snapshotMode ? 'dataInput.uploadConfirm.snapshotTitle' : 'dataInput.uploadConfirm.title';
  const resultMessage = resultPresentation?.messageKey
    ? t(resultPresentation.messageKey, resultPresentation.params)
    : feedback?.messageKey
      ? t(feedback.messageKey, feedback.params)
      : feedback?.message || translateMessage(
        resultPresentation?.messageCode || 'IMPORT_PROCESSING_FAILED',
        500,
        { year, ...(resultPresentation?.params || {}) },
      );

  return (
    <>
      <AppDialog
        open={open}
        onClose={requestClose}
        TransitionProps={{ onExited }}
        maxWidth="md"
        fullWidth
        sx={{ '& .MuiButton-root': { borderRadius: '6px' } }}
      >
        <DialogTitle sx={{ pr: 6 }}>
          {t(titleKey)}
          <IconButton
            onClick={requestClose}
            aria-label={t('common.close')}
            sx={{ position: 'absolute', right: 12, top: 12 }}
          >
            <CloseRoundedIcon fontSize="small" />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers>
          <Stack spacing={2}>
            {snapshotMode ? (
              <SnapshotSummary
                sourceTitle={sourceTitle}
                year={year}
                files={files}
              />
            ) : (
              <ImportSummary
                sourceTitle={sourceTitle}
                year={year}
                fileName={fileName}
                fileSize={fileSize}
              />
            )}

            {isConfirming && snapshotMode && snapshotChanged && (
              <Alert severity="warning">{t('dataInput.scope3Af.snapshotChanged')}</Alert>
            )}

            {isConfirming && (feedback?.message || snapshotMode || replacesYear) && (
              <Alert severity={feedback?.severity || (snapshotMode || replacesYear ? 'warning' : 'info')}>
                {feedback?.message || (snapshotMode
                  ? t('dataInput.scope3Af.fullSnapshotWarning', { year })
                  : t('dataInput.uploadConfirm.replacementWarning', {
                    file: currentName || t('dataInput.history.notAvailable'),
                  }))}
              </Alert>
            )}

            {showProcessing && (
              <Stack spacing={1}>
                <Typography variant="body2" fontWeight={700}>
                  {t(`dataInput.operation.status.${activeStatus}`, {
                    defaultValue: activeStatus,
                  })}
                </Typography>
                <LinearProgress
                  variant={hasDeterminateProgress ? 'determinate' : 'indeterminate'}
                  value={progressValue}
                  aria-label={t('dataInput.importDialog.progress')}
                />
                {hasDeterminateProgress && (
                  <Typography variant="caption" color="text.secondary">
                    {processedRows.toLocaleString()}/{totalRows.toLocaleString()}
                  </Typography>
                )}
                {monitoringError && (
                  <Alert severity="warning">{t('dataInput.importDialog.statusUnavailable')}</Alert>
                )}
              </Stack>
            )}

            {isTerminal && (
              <>
                <Alert severity={resultPresentation.severity}>{resultMessage}</Alert>
                {isPartial && resultPresentation?.causeCode && (
                  <Typography variant="body2" color="text.secondary">
                    {translateMessage(resultPresentation.causeCode, 500, resultPresentation.params)}
                  </Typography>
                )}
                {showResultTable && (
                  <ResultSummaryTable
                    sourceId={sourceId}
                    data={resultData}
                    loading={resultLoading}
                    error={resultError}
                    onRetry={reloadResult}
                  />
                )}
              </>
            )}
          </Stack>
        </DialogContent>
        <DialogActions sx={{ px: { xs: 2, sm: 3 }, py: 1.5 }}>
          {isConfirming ? (
            <>
              <Button onClick={requestClose}>{t('common.cancel')}</Button>
              <Button variant="contained" onClick={onConfirm}>
                {t('dataInput.uploadConfirm.confirm')}
              </Button>
            </>
          ) : (
            <>
              {isPartial && onReviewHistory && (
                <Button onClick={onReviewHistory}>
                  {t('dataInput.importDialog.reviewHistory')}
                </Button>
              )}
              {isPartial && onRefreshData && (
                <Button variant="contained" onClick={onRefreshData}>
                  {t('dataInput.importDialog.refreshData')}
                </Button>
              )}
              {!isPartial && showProcessing && monitoringError && onRetryStatus && (
                <Button onClick={onRetryStatus}>{t('dataInput.importDialog.retryStatus')}</Button>
              )}
              {!isPartial && (
                <Button onClick={requestClose}>{t('common.close')}</Button>
              )}
            </>
          )}
        </DialogActions>
      </AppDialog>
      <ProcessingCloseWarningDialog
        open={isWarningOpen}
        onStay={stay}
        onCloseAnyway={closeAnyway}
      />
    </>
  );
}
